import { useContext, useCallback } from 'react';
import { DrawerContext } from '../contexts/DrawerContext';
import type { DrawerConfig, DrawerContextType } from '../contexts/DrawerContext';

/**
 * 自定义Hook，用于在任何组件中方便地使用全局Drawer
 * 
 * @example
 * const { openDrawer, closeDrawer, updateConfig } = useDrawer();
 * 
 * // 打开Drawer
 * openDrawer({
 *   title: '添加设备',
 *   content: <DeviceForm />,
 *   width: 800,
 * });
 * 
 * // 更新Drawer配置
 * updateConfig({ title: '新标题' });
 */
export const useDrawer = () => {
  const context = useContext(DrawerContext);
  
  if (!context) {
    throw new Error('useDrawer必须在DrawerProvider内部使用');
  }
  
  // 提供便捷的打开方法
  const openDrawerWithDefaults = useCallback((config: DrawerConfig) => {
    const defaultConfig: DrawerConfig = {
      width: 720,
      maskClosable: false,
      closable: true,
      keyboard: true,
      placement: 'right',
      zIndex: 1000,
      ...config,
    };
    context.openDrawer(defaultConfig);
  }, [context]);

  return {
    ...context,
    openDrawer: openDrawerWithDefaults,
  } as DrawerContextType;
};

